import { RacemapColors } from '@racemap/utilities/consts/common';
import { shortIdBuilder } from '@racemap/utilities/functions/utils';
import type {
  GeoObjects,
  PointObject,
  PointOnLineStringObject,
} from '@racemap/utilities/types/geos';
import type { Point } from '@racemap/utilities/types/types';
import type { Feature, FeatureCollection, Point as GeojsonPoint, Position } from 'geojson';
import { type FC, useContext, useEffect, useMemo, useRef } from 'react';
import { Layer, type MapLayerMouseEvent, Source } from 'react-map-gl';
import { MapContext, type NewDataProps } from '../BasicMap/BasicMap';
import { type AUTOFOCUS, convertPointsToFeatures, injectFeatureIds } from '../helpers';

export type PointProperties = Record<string, unknown> & {
  lineWidth?: number;
  color?: string;
};

export type PointLayerPoints =
  | null
  | Position
  | Array<Position>
  | Feature<GeojsonPoint, PointProperties>
  | Array<Feature<GeojsonPoint, PointProperties> | PointObject | PointOnLineStringObject>
  | FeatureCollection<GeojsonPoint, PointProperties>
  | PointOnLineStringObject
  | PointObject
  | GeoObjects
  | Array<Point>;

export interface Props {
  points: PointLayerPoints;
  color?: string;
  fillColor?: string;
  lineWidth?: number;
  radius?: number;
  beforeId?: string;
  visible?: boolean;
  onNewData?: (props: NewDataProps) => void;
  onMouseEnter?: (evt: MapLayerMouseEvent) => void;
  onMouseLeave?: (evt: MapLayerMouseEvent) => void;
  onClick?: (evt: MapLayerMouseEvent) => void;
  autofocus?: boolean | AUTOFOCUS;
  id?: string;
}

export const PointLayer: FC<Props> = ({
  points,
  color = RacemapColors.PaleBlue,
  fillColor = RacemapColors.CloudBlue,
  lineWidth = 2,
  radius = 2,
  beforeId,
  visible = true,
  onNewData,
  autofocus = false,
  onMouseEnter,
  onMouseLeave,
  onClick,
  id,
}) => {
  const idRef = useRef(id || shortIdBuilder());
  const { features, paint } = useMemo(
    () => convertPointsToFeatures(points, color, lineWidth, fillColor, radius),
    [points, color, lineWidth, fillColor, radius],
  );
  const featuresWithIdProperty = useMemo(() => injectFeatureIds(features), [features]);
  const featureCollection: FeatureCollection<GeojsonPoint, PointProperties> = {
    type: 'FeatureCollection',
    features: [...featuresWithIdProperty],
  };
  const {
    setMouseEnterAction,
    setMouseLeaveAction,
    setOnClickAction,
    addInteractiveLayerIds,
    mapStyle,
  } = useContext(MapContext);
  const layers = mapStyle?.layers.map((l) => l.id) || [];

  useEffect(() => {
    addInteractiveLayerIds(`layer-point-${idRef.current}`);
  }, []);

  useEffect(() => {
    onNewData?.({ layer: `layer-point-${idRef.current}`, data: featureCollection, autofocus });
  }, [featureCollection]);

  useEffect(() => {
    if (onMouseEnter != null) {
      setMouseEnterAction(`point-${idRef.current}`, onMouseEnter);
    }
    if (onMouseLeave != null) {
      setMouseLeaveAction(`point-${idRef.current}`, onMouseLeave);
    }
    if (onClick != null) {
      setOnClickAction(`point-${idRef.current}`, onClick);
    }
  }, [onMouseEnter, onMouseLeave, onClick]);

  if (!visible) return;

  return (
    <Source
      type="geojson"
      data={featureCollection}
      id={`point-${idRef.current}`}
      key={`point-${idRef.current}`}
      promoteId={'_id'}
    >
      <Layer
        id={`layer-point-${idRef.current}`}
        key={`layer-point-${idRef.current}`}
        type="circle"
        paint={paint}
        beforeId={beforeId != null && layers.includes(beforeId) ? beforeId : undefined}
      />
    </Source>
  );
};
